import { IN_STOCK_WAREHOUSE_FIELD } from './categoryPageTypes';

const variantHasWarehouse = (variant, field, targetWarehouseIds) => {
  const warehouseIds = variant?.[field];

  if (!Array.isArray(warehouseIds)) {
    return false;
  }

  return warehouseIds.some(id => targetWarehouseIds.has(String(id)));
};

/**
 * Whether any SKU on the hit is stocked at one of the given warehouses.
 * Same field the Ready-to-Ship filter reads (`in_stock_warehouseIds`).
 */
export const hitHasInStockAtWarehouseIds = (hit, warehouseIds) => {
  const targets = new Set(
    (warehouseIds || []).filter(id => id != null && id !== '').map(String)
  );

  if (!targets.size) {
    return false;
  }

  const variants = hit?.product_variants;

  if (!Array.isArray(variants)) {
    return false;
  }

  return variants.some(variant =>
    variantHasWarehouse(variant, IN_STOCK_WAREHOUSE_FIELD, targets)
  );
};

/**
 * Returns the entries of a hit's `product_variants` array whose warehouse list
 * contains the user's warehouse.
 *
 * The `products_jb4` index is product-level, but the clearance /
 * ready-to-ship / flash-sale page types are SKU-specific: a product belongs on
 * the page because particular SKUs qualify at the user's warehouse. The
 * per-SKU warehouse arrays let the grid pick those SKUs from the hit itself,
 * which is the only place the correlation exists: Algolia matches fields
 * inside arrays of objects independently, so `product_variants.sku:X AND
 * product_variants.in_stock_warehouseIds:154` can be satisfied by two
 * *different* variants and cannot express "this SKU, at this warehouse".
 *
 * The qualifying SKUs are then matched against the enriched (upholstery-only)
 * variants so the product card shows only the matched upholstery options.
 * Variants are returned verbatim — SKU normalization for that join belongs to
 * the consumers (`useDisplayProducts`, `buildProductsInAlgoliaOrder`), which
 * need the raw entry's warehouse arrays too.
 *
 * `variantWarehouseField` is the unprefixed field name inside a
 * `product_variants` entry, owned by the page-type descriptor so the filter
 * clause and this matching share one source of truth.
 */
const getQualifyingHitVariants = ({
  hit,
  warehouseId,
  variantWarehouseField,
}) => {
  if (!variantWarehouseField || warehouseId == null || warehouseId === '') {
    return [];
  }

  const variants = hit?.product_variants;

  if (!Array.isArray(variants)) {
    return [];
  }

  // Normalized on both sides: `warehouseId` is a string client-side, but the
  // indexed array's element type is not guaranteed. An unnormalized compare
  // returns empty rather than failing loudly.
  const targetWarehouseIds = new Set([String(warehouseId)]);

  return variants.filter(variant =>
    variantHasWarehouse(variant, variantWarehouseField, targetWarehouseIds)
  );
};

export default getQualifyingHitVariants;
